/* voyado-atc.js
 * Listens for cart:item-added and sends a Voyado add-to-cart notification
 * using the ticket stored in sessionStorage when the user last clicked a
 * Voyado-rendered product link.
 *
 * The notification is sent via window.elevate.notify.addToCart(ticket), which
 * auto-attaches market + customerKey/sessionKey and sends with keepalive.
 */

const TICKET_KEY = "voyado_pending_atc_ticket";

document.addEventListener("cart:item-added", async (e) => {
  let ticket = e.detail?.voyadoTicket ?? null;

  // PDP buy button: the ticket comes from window.voyadoVariantTicketBySku, which the
  // product-page slider only populates after its async recommendation query resolves.
  // If the user added to cart before that query finished we get a SKU but no ticket —
  // poll the lookup briefly so a slow recs query no longer drops the notification.
  if (!ticket && e.detail?.voyadoSku) {
    ticket = await waitForVariantTicket(e.detail.voyadoSku);
  }

  if (!ticket) {
    try {
      ticket = sessionStorage.getItem(TICKET_KEY);
      if (ticket) sessionStorage.removeItem(TICKET_KEY);
    } catch {}
  }
  if (!ticket) return;

  // Auto-attaches market + customerKey/sessionKey; sends with keepalive.
  window.elevate?.notify?.addToCart(ticket);
});

// Poll window.voyadoVariantTicketBySku until it yields a ticket for this SKU.
// Resolves null immediately when no product-page slider exists on the page (no
// ticket will ever arrive), otherwise polls up to ~2s. Fire-and-forget: the
// caller has already emitted cart:item-added, so this never blocks the UI.
function waitForVariantTicket(sku, timeoutMs = 2000, stepMs = 100) {
  if (!document.querySelector('voyado-slider[data-product-key]:not([data-product-key=""])')) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const t = window.voyadoVariantTicketBySku?.(sku);
      if (t) return resolve(t);
      if (Date.now() - start >= timeoutMs) return resolve(null);
      setTimeout(tick, stepMs);
    };
    tick();
  });
}

// Mirrors voyado-consent.js's gate: while analytics consent is withheld,
// window.elevate.query.c.session() returns a fresh random-UUID pair instead of
// the real persisted session — and that pair is just as truthy as a real one.
// Cart attribution must not be stamped with that throwaway value.
function hasAnalyticsConsent() {
  var cp = window.Shopify && window.Shopify.customerPrivacy;
  var v = cp && cp.currentVisitorConsent && cp.currentVisitorConsent();
  return !!(v && (v.analytics === 'yes' || v.analytics === true));
}

// Elevate session keys for cart-attribute stamping (a Shopify cart write, not a
// Voyado API call, so it can't go through notify/query). session() is the SDK's
// localStorageBackedSession accessor — returns the persisted { customerKey,
// sessionKey } and reflects session.updateCustomerKey() after sign-in. Returns
// null while consent is withheld so the caller never stamps the gated random
// fallback onto the cart.
function elevateKeys() {
  if (!hasAnalyticsConsent()) return null;
  const s = window.elevate?.query?.c?.session?.();
  return s && s.customerKey && s.sessionKey
    ? { customerKey: s.customerKey, sessionKey: s.sessionKey }
    : null;
}

/* -------------------------------------------------------------------------
 * Elevate cart-attribute stamping
 *
 * Writes _elevate_customer_key / _elevate_session_key / _elevate_market onto
 * the cart as cart attributes. These persist onto the order at checkout, which
 * is how Elevate attributes the purchase back to the recommendation session.
 *
 * Only stamps when there is real tracking consent and a real Elevate session —
 * the anonymous random-UUID fallback used elsewhere is useless for attribution.
 * ---------------------------------------------------------------------- */

// Stamped once per pageview; short-circuits repeat triggers.
let elevateAttrsStamped = false;

/**
 * Ensure the cart carries the _elevate_* attributes.
 * @param {{ requireExisting?: boolean }} [opts]
 *   requireExisting — when true, only stamp a cart that already has items
 *   (used on initial load so we never create empty carts for passers-by).
 */
async function ensureElevateCartAttributes({ requireExisting = false } = {}) {
  if (elevateAttrsStamped) return;

  const sdk = window.elevate?.query?.c;
  if (!sdk) return;

  // Real persisted keys from the Elevate SDK session — required for attribution.
  const keys = elevateKeys();
  const market = sdk.market;
  if (!keys || !market) return;
  const { customerKey, sessionKey } = keys;

  let cart;
  try {
    const resp = await fetch("/cart.js", {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    cart = await resp.json();
  } catch {
    return;
  }

  if (requireExisting && (cart?.item_count ?? 0) === 0) return;

  // Already stamped on the cart — nothing to do.
  const a = cart?.attributes || {};
  if (a._elevate_customer_key && a._elevate_session_key && a._elevate_market) {
    elevateAttrsStamped = true;
    return;
  }

  try {
    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: {
          _elevate_customer_key: customerKey,
          _elevate_session_key: sessionKey,
          _elevate_market: market,
        },
      }),
    });
    elevateAttrsStamped = true;
  } catch {
    // Non-fatal — attribution is best-effort and must never break the cart.
  }
}

// Re-stamp whenever an item is added (cart is guaranteed non-empty here).
document.addEventListener("cart:item-added", () => {
  ensureElevateCartAttributes();
});

// Covers: visitor lands on a PDP, adds to cart (consent still undecided, so
// ensureElevateCartAttributes() above no-ops), then accepts cookies. The SDK's
// real session only materializes once consent is granted, so re-attempt the
// stamp then — this event fires only on an actual consent change, never on
// listener attach, so it can't race the initial no-op above.
document.addEventListener("visitorConsentCollected", (e) => {
  if (e.detail?.analyticsProcessingAllowed) {
    ensureElevateCartAttributes({ requireExisting: true });
  }
});

// On load, stamp a pre-existing cart once the SDK is ready. The Elevate SDK is
// injected by the app embed and may not be present when this deferred script
// first runs, so poll briefly (~3s) before giving up.
(function stampExistingCartWhenReady() {
  let tries = 0;
  const id = setInterval(() => {
    if (window.elevate?.query?.c) {
      clearInterval(id);
      ensureElevateCartAttributes({ requireExisting: true });
    } else if (++tries >= 30) {
      clearInterval(id);
    }
  }, 100);
})();
